import React, { useState } from 'react';
import ToolLayout from '../../components/ToolLayout';
import UsageIndicator from '../../components/UsageIndicator';
import ModeSelector from '../../components/ModeSelector';
import MarkdownRenderer from '../../components/MarkdownRenderer';
import { Mail, MessageSquare, Sparkles } from 'lucide-react';
import { auth } from '../../lib/firebase';
import { toast } from 'sonner';
import { authenticatedApiCall } from '../../lib/apiClient';
import { useUser } from '@/context/UserContext';
import { emitUsageRefresh } from '@/lib/usageEvents';

const EmailGenerator = () => {
  const [receiver, setReceiver] = useState('');
  const [goal, setGoal] = useState('');
  const [tone, setTone] = useState('professional');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [mode, setMode] = useState('basic');

  const { plan, loading: userLoading, authReady } = useUser();

  if (userLoading || !authReady) {
    return (
      <ToolLayout title="Email Generator">
        <div className="text-center py-16 text-white">Loading…</div>
      </ToolLayout>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!auth.currentUser) {
      toast.error('Please login to use this tool');
      return;
    }

    setLoading(true);
    setResult('');

    try {
      const data = await authenticatedApiCall('/api/generate-email', {
        receiver,
        goal,
        tone,
        mode, // ✅ basic or deep
      });

      setResult(data.email || '');

      emitUsageRefresh({
        tool: 'email',
        mode,
      });

      toast.success('Email generated');
    } catch (err) {
      toast.error('Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolLayout title="Email Generator">
      <div className="max-w-4xl mx-auto">
        <UsageIndicator />

        <ModeSelector
          mode={mode}
          setMode={setMode}
          subscription={{ plan, status: 'active' }}
        />

        <div className="rounded-2xl p-6 mb-8 bg-accent-teal/10 border border-accent-teal/30">
          <div className="flex gap-4">
            <Sparkles className="text-accent-teal mt-1" />
            <div>
              <h2 className="text-xl font-bold text-white mb-2">How it works</h2>
              <p className="text-neutral-silver">
                Enter the recipient and your goal. The AI will generate a properly
                structured, professional email with subject, body, and signature.
                Deep Research mode will search the web for relevant context about
                the person, role, or company before writing.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 mb-8">
          <Input
            label="Who are you emailing?"
            icon={Mail}
            value={receiver}
            onChange={setReceiver}
            placeholder="e.g. Marketing Manager at a SaaS company"
          />

          <Textarea
            label="What is your goal?"
            icon={MessageSquare}
            value={goal}
            onChange={setGoal}
            placeholder="e.g. Request a 20-minute demo call"
          />

          <div>
            <label className="text-white font-medium mb-2 block">Tone</label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-neutral-900 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-accent-teal"
            >
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="persuasive">Persuasive</option>
              <option value="casual">Casual</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-lg bg-accent-teal text-white font-semibold disabled:opacity-50"
          >
            {loading ? 'Generating…' : 'Generate Email'}
          </button>
        </form>

        {result && (
          <div className="rounded-2xl p-6 bg-white/5 border border-accent-teal/20">
            <h2 className="text-2xl font-bold text-white mb-4">
              Generated Email
            </h2>
            <MarkdownRenderer content={result} />
          </div>
        )}
      </div>
    </ToolLayout>
  );
};

const Input = ({ label, value, onChange, icon: Icon, placeholder }) => (
  <div>
    <label className="text-white font-medium mb-2 flex gap-2">
      <Icon size={18} /> {label}
    </label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required
      className="w-full px-4 py-3 rounded-lg bg-white/5 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-accent-teal"
    />
  </div>
);

const Textarea = ({ label, value, onChange, icon: Icon, placeholder }) => (
  <div>
    <label className="text-white font-medium mb-2 flex gap-2">
      <Icon size={18} /> {label}
    </label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={4}
      required
      className="w-full px-4 py-3 rounded-lg bg-white/5 text-white border border-white/10 resize-none focus:outline-none focus:ring-2 focus:ring-accent-teal"
    />
  </div>
);

export default EmailGenerator;
